/**
 * PropertiesPage.tsx
 *
 * Página de gestión de propiedades inmobiliarias.
 * Funcionalidades:
 * - CRUD completo de propiedades
 * - Upload múltiple de imágenes (integrado con Google Cloud Storage)
 * - Generación de códigos QR con modal premium
 * - Filtros y búsqueda
 * - Control de expiración de publicaciones (max 30 días)
 * - Ubicación por coordenadas con reverse geocoding (OpenStreetMap)
 * - Campos para integración con portales (Inmuebles24, Lamudi, etc.)
 */
import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Edit3,
  Trash2,
  Camera,
  MapPin,
  Bed,
  Bath,
  Toilet,
  Square,
  DollarSign,
  Eye,
  Filter,
  Search,
  X,
  ChevronDown,
  QrCode,
  Clock,
  Star,
  AlertCircle,
  Loader2,
  Car,
  Building,
  PawPrint,
  Sofa,
  Calendar
} from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import api from '@/services/api'
import QRModal from '@/components/QRModal'
import ShareButton from '@/components/ShareButton'
import ImageUploadModal from '@/components/ImageUploadModal'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

interface Property {
  id: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  area_m2?: number
  zone: string
  address?: string
  images: string[]
  featured_image?: string
  purpose: string
  status: string
  construction_status?: string
  views_count: number
  published_at: string
  expires_at?: string
  qr_code_url?: string
  seo_title: string
  public_listing: boolean
  priority_level?: 'standard' | 'premium' | 'featured'
  latitude?: number
  longitude?: number
  city?: string
  state?: string
  country?: string
  area_total?: number
  parking_spots?: number
  amenities?: string[]
  year_built?: number
  floor_number?: number
  total_floors?: number
  maintenance_fee?: number
  furnished?: string
  pets_allowed?: boolean
}

const EXPIRY_CONFIG = {
  MAX_DAYS: 30,
  WARNING_DAYS: 5,
  URGENT_DAYS: 2
}

const getDefaultExpiryDate = () => {
  const d = new Date()
  d.setDate(d.getDate() + 30)
  return d.toISOString().split('T')[0]
}

const DEFAULT_FORM_VALUES = {
  title: '',
  description: '',
  price: '',
  currency: 'MXN',
  property_type: 'Casa',
  bedrooms: '',
  bathrooms: '',
  half_bathrooms: '',
  area_m2: '',
  zone: '',
  address: '',
  purpose: '',
  status: 'available',
  construction_status: '',	
  public_listing: true,
  expires_at: '',
  priority_level: 'standard',
  latitude: '',
  longitude: '',
  city: '',
  state: '',
  country: 'México',
  area_total: '',
  parking_spots: '',
  amenities: '',
  year_built: '',
  floor_number: '',
  total_floors: '',
  maintenance_fee: '',
  furnished: '',
  pets_allowed: false
}

const AMENITY_SUGGESTIONS = [
  'Alberca', 'Jardín', 'Gimnasio', 'Roof Garden', 'Área de juegos',
  'Salón de eventos', 'Seguridad 24/7', 'Portero', 'Elevador',
  'Cuarto de servicio', 'Bodega', 'Cisterna', 'Calentador solar',
  'Aire acondicionado', 'Chimenea', 'Terraza', 'Balcón', 'Cocina integral'
]

const PropertiesPage: React.FC = () => {
  const { agent } = useAuth()
  const { t } = useTranslation('properties')
  const isSeller = agent?.agent_type === 'seller'
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingProperty, setEditingProperty] = useState<Property | null>(null)
  const [viewOnly, setViewOnly] = useState(false)
  const [formData, setFormData] = useState(DEFAULT_FORM_VALUES)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [showFilters, setShowFilters] = useState(false)
  const [showExpired, setShowExpired] = useState(true)

  // Modal reactivar
  const [showReactivateModal, setShowReactivateModal] = useState(false)
  const [reactivatingProperty, setReactivatingProperty] = useState<Property | null>(null)
  const [reactivateDate, setReactivateDate] = useState('')

  const [generatingQR, setGeneratingQR] = useState<string | null>(null)
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedPropertyForQR, setSelectedPropertyForQR] = useState<Property | null>(null)
  const [qrBase64Data, setQrBase64Data] = useState<{ base64: string; url: string } | null>(null)

  const [showImageModal, setShowImageModal] = useState(false)
  const [selectedPropertyForImages, setSelectedPropertyForImages] = useState<Property | null>(null)

  const [loadingGeo, setLoadingGeo] = useState(false)
  const [geoError, setGeoError] = useState<string | null>(null)

  const location = useLocation()
  const processedEditParam = React.useRef<string | null>(null)

  useEffect(() => {
    fetchProperties()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const editSeoTitle = params.get('edit')
    if (editSeoTitle && properties.length > 0 && processedEditParam.current !== editSeoTitle) {
      const prop = properties.find(p => p.seo_title === editSeoTitle)
      if (prop) {
        processedEditParam.current = editSeoTitle
        handleOpenForm(prop)
        window.history.replaceState({}, '', '/dashboard/properties')
      }
    }
  }, [location.search, properties])

  const fetchProperties = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/agents/properties')
      setProperties(response.data)
    } catch (error) {
      console.error('Error fetching properties:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchLocationFromCoords = async () => {
    const lat = parseFloat(formData.latitude)
    const lng = parseFloat(formData.longitude)

    if (isNaN(lat) || isNaN(lng)) {
      setGeoError(t('invalidCoords'))
      return
    }

    setLoadingGeo(true)
    setGeoError(null)

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'es',
            'User-Agent': 'InnovAgent/1.0'
          }
        }
      )

      if (!response.ok) throw new Error('Error en geocoding')

      const data = await response.json()

      if (data.error) {
        setGeoError(t('noLocationFound'))
        return
      }

      const address = data.address || {}
      const city = address.city || address.municipality || address.county || address.town || address.village || ''
      const state = address.state || ''
      const country = address.country || 'México'
      const zone = address.suburb || address.neighbourhood || address.quarter || ''
      const streetAddress = address.road ? `${address.road} ${address.house_number || ''}`.trim() : ''

      setFormData(prev => ({
        ...prev,
        city,
        state,
        country,
        zone: zone || prev.zone,
        address: streetAddress || prev.address
      }))

    } catch (error) {
      console.error('Error fetching location:', error)
      setGeoError(t('geoError')) 
    } finally {
      setLoadingGeo(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const amenitiesArray = formData.amenities
        ? formData.amenities.split(',').map(a => a.trim()).filter(a => a.length > 0)
        : []

      const propertyData = {
        ...formData,
        price: parseFloat(formData.price),
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        half_bathrooms: formData.half_bathrooms ? parseInt(formData.half_bathrooms) : 0,
        area_m2: formData.area_m2 ? parseFloat(formData.area_m2) : null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        city: formData.city || null,
        state: formData.state || null,
        country: formData.country || 'México',
        area_total: formData.area_total ? parseFloat(formData.area_total) : null,
        parking_spots: formData.parking_spots ? parseInt(formData.parking_spots) : null,
        amenities: amenitiesArray,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        floor_number: formData.floor_number ? parseInt(formData.floor_number) : null,
        total_floors: formData.total_floors ? parseInt(formData.total_floors) : null,
        maintenance_fee: formData.maintenance_fee ? parseFloat(formData.maintenance_fee) : null,
        furnished: formData.furnished || null,
        pets_allowed: formData.pets_allowed,
        construction_status: formData.construction_status || null
      }

      if (editingProperty) {
        await api.put(`/api/agents/properties/${editingProperty.id}`, propertyData)
      } else {
        await api.post('/api/agents/properties', propertyData)
      }
      setShowForm(false)
      setEditingProperty(null)
      setFormData(DEFAULT_FORM_VALUES)
      setGeoError(null)
      toast.success(editingProperty ? t('propertyUpdated') : t('propertyCreated'))
      await fetchProperties()
   } catch (error: any) {
      console.error('Error saving property:', error)
      const data = error.response?.data
      if (data?.error === 'plan_limit_reached') {
        toast.warning(data.message)
      } else {
        toast.error(t('errorSaving')) 
      }
    }
  }

  const handleOpenForm = (property: Property, readOnly = false) => {
    setFormData({
      title: property.title,
      description: property.description,
      price: property.price.toString(),
      currency: property.currency,
      property_type: property.property_type,
      bedrooms: property.bedrooms?.toString() || '',
      bathrooms: property.bathrooms?.toString() || '',
      half_bathrooms: property.half_bathrooms?.toString() || '',
      area_m2: property.area_m2?.toString() || '',
      zone: property.zone || '',
      address: property.address || '',
      purpose: property.purpose,
      status: property.status,
      construction_status: property.construction_status || '',
      expires_at: property.expires_at ? new Date(property.expires_at).toISOString().split('T')[0] : '',
      public_listing: property.public_listing !== false,
      priority_level: property.priority_level || 'standard',
      latitude: property.latitude?.toString() || '',
      longitude: property.longitude?.toString() || '',
      city: property.city || '',
      state: property.state || '',
      country: property.country || 'México',
      area_total: property.area_total?.toString() || '',
      parking_spots: property.parking_spots?.toString() || '',
      amenities: property.amenities?.join(', ') || '',
      year_built: property.year_built?.toString() || '',
      floor_number: property.floor_number?.toString() || '',
      total_floors: property.total_floors?.toString() || '',
      maintenance_fee: property.maintenance_fee?.toString() || '',
      furnished: property.furnished || '',
      pets_allowed: property.pets_allowed || false
    })
    setEditingProperty(property)
    setViewOnly(readOnly)
    setShowForm(true)
    setGeoError(null)
  }

  const handleReactivate = (property: Property) => {
    setReactivatingProperty(property)
    setReactivateDate(getDefaultExpiryDate())
    setShowReactivateModal(true)
  }

  const submitReactivate = async () => {
    if (!reactivatingProperty || !reactivateDate) return

    // Validación cliente: máximo 45 días desde hoy
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const maxDate = new Date(today)
    maxDate.setDate(maxDate.getDate() + 45)
    const selected = new Date(reactivateDate)

    if (selected < today) {
      toast.error(t('expiryNotBeforeToday'))
      return
    }
    if (selected > maxDate) {
      const maxStr = maxDate.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
      toast.error(t('expiryMaxDate', { date: maxStr })) 
      return
    }

    try {
      await api.patch(`/api/agents/properties/${reactivatingProperty.id}/reactivate`, {
        expires_at: reactivateDate
      })
      toast.success(t('propertyReactivated'))
      setShowReactivateModal(false)
      setReactivatingProperty(null)
      fetchProperties()
    } catch (error: any) {
      const msg = error?.response?.data?.error || t('errorReactivating')
      console.error('Error reactivating property:', error)
      toast.error(msg)
    }
  }

  const deleteProperty = async (id: string) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await api.delete(`/api/agents/properties/${id}`)
        toast.success(t('propertyDeleted'))
        fetchProperties()
      } catch (error: any) {
        const msg = error?.response?.data?.error || t('errorDeleting')
        console.error('Error deleting property:', error)
        toast.error(msg)
      }
    }
  }

  const generateQR = async (propertyId: string): Promise<{ base64: string; url: string } | null> => {
    setGeneratingQR(propertyId)
    try {
      const response = await api.post(`/api/agents/properties/${propertyId}/generate-qr`)
      if (response.data.success) {
        return {
          base64: response.data.qrBase64,
          url: response.data.propertyURL
        }
      }
      return null
    } catch (error) {
      console.error('Error generating QR:', error)
      toast.error(t('errorQR'))
      return null
    } finally {
      setGeneratingQR(null)
    }
  }

  const handleQRClick = async (property: Property) => {
    const qrData = await generateQR(property.id)
    if (qrData) {
      setSelectedPropertyForQR(property)
      setQrBase64Data(qrData)
      setShowQRModal(true)
    }
  }

  const openImageUpload = (property: Property) => {
    setSelectedPropertyForImages(property)
    setShowImageModal(true)
  }

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price)
  }

  const getTimeUntilExpiry = (expiresAt: string) => {
    const now = new Date()
    const expiry = new Date(expiresAt)
    const diffTime = expiry.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return { text: t('expired'), color: 'text-red-400', urgent: true, expired: true }
    if (diffDays <= EXPIRY_CONFIG.URGENT_DAYS) return { text: t('daysLeft', { count: diffDays }), color: 'text-red-400', urgent: true, expired: false }
    if (diffDays <= EXPIRY_CONFIG.WARNING_DAYS) return { text: t('daysLeft', { count: diffDays }), color: 'text-yellow-400', urgent: true, expired: false }
    return { text: t('daysLeft', { count: diffDays }), color: 'text-green-400', urgent: false, expired: false }
  }

  const getPropertyUrl = (property: Property) => {
    const subdomain = agent?.subdomain || 'demo'
    return `https://${subdomain}.innovagent.tech/property/${property.seo_title}`
  }

  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.zone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.city?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || property.property_type === filterType

    if (!matchesSearch || !matchesType) return false

    if (!showExpired && property.expires_at) {
      const expiryInfo = getTimeUntilExpiry(property.expires_at)
      if (expiryInfo.expired) return false
    }

    return true
  })

  // Tres grupos: activas, vencidas, no disponibles
  const activeProperties = filteredProperties.filter(p => {
    if (p.status === 'sold' || p.status === 'rented') return false
    if (!p.expires_at) return true
    return !getTimeUntilExpiry(p.expires_at).expired
  })

  const expiredProperties = filteredProperties.filter(p => {
    if (p.status === 'sold' || p.status === 'rented') return false
    if (!p.expires_at) return false
    return getTimeUntilExpiry(p.expires_at).expired
  })

  const unavailableProperties = filteredProperties.filter(p =>
    p.status === 'sold' || p.status === 'rented'
  )

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">{t('myProperties')}</h1>
            <p className="text-white/70">{t('manageCatalog')}</p>
          </div>

          {!isSeller && (
            <button
              onClick={() => {
                setFormData({ ...DEFAULT_FORM_VALUES, expires_at: getDefaultExpiryDate() })
                setEditingProperty(null)
                setViewOnly(false)
                setShowForm(true)
              }}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-xl text-white font-semibold transition-all shadow-lg hover:shadow-xl"
            >
              <Plus className="h-5 w-5" />
              {t('newProperty')}
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <GlassCard className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/50" />
              <input
                type="text"
                placeholder={t('searchProperties')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
            >
              <Filter className="h-5 w-5" />
              {t('filters')}
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-4"
            >
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
              >
                <option value="all" className="bg-slate-800 text-white">{t('allTypes')}</option>
                <option value="Casa" className="bg-slate-800 text-white">{t('typeHouse')}</option> 
                <option value="Departamento" className="bg-slate-800 text-white">{t('typeApartment')}</option> 
                <option value="Terreno" className="bg-slate-800 text-white">{t('typeLand')}</option>
                <option value="Comercial" className="bg-slate-800 text-white">{t('typeCommercial')}</option>
              </select>

              <label className="flex items-center gap-2 text-white cursor-pointer">
                <input
                  type="checkbox"
                  checked={showExpired}
                  onChange={(e) => setShowExpired(e.target.checked)}
                  className="w-4 h-4"
                />
                {t('showExpired')}
              </label>
            </motion.div>
          )}
        </GlassCard>

        {/* Properties Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white/70">{t('loadingProperties')}</p>
          </div>
        ) : filteredProperties.length === 0 ? (
          <GlassCard>
            <div className="text-center py-12">
              <MapPin className="h-16 w-16 mx-auto mb-4 text-white/30" />
              <h3 className="text-xl font-semibold text-white mb-2">{t('noProperties')}</h3>
              <p className="text-white/70 mb-6">
                {isSeller ? t('noPropertiesSeller') : t('noPropertiesAgent')}
              </p>
              {!isSeller && (
                <button
                  onClick={() => {
                    setFormData({ ...DEFAULT_FORM_VALUES, expires_at: getDefaultExpiryDate() })
                    setShowForm(true)
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-semibold transition-colors"
                >
                  <Plus className="h-5 w-5" />
                  {t('newProperty')}
                  </button>
              )}
            </div>
          </GlassCard>
        ) : (
          <>
            {/* Propiedades Activas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeProperties.map((property) => {
                const expiryInfo = property.expires_at ? getTimeUntilExpiry(property.expires_at) : null
                return (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="group"
                  >
                    <GlassCard className="overflow-hidden hover:shadow-2xl transition-shadow">
                      <div className="relative h-48 bg-gradient-to-br from-gray-700 to-gray-900 overflow-hidden">
                        {property.featured_image || property.images?.[0] ? (
                          <img
                            src={property.featured_image || property.images[0]}
                            alt={property.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <MapPin className="h-16 w-16 text-white/30" />
                          </div>
                        )}

                        <div className="absolute top-3 left-3 flex flex-col gap-2"> 
                          {property.priority_level && property.priority_level !== 'standard' && (
                            <div className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                              property.priority_level === 'featured' ? 'bg-red-500' : 'bg-orange-500'
                            }`}>
                              <Star className="h-3 w-3" />
                              {property.priority_level === 'featured' ? t('featured') : t('premium')}
                            </div>
                          )}
                          {property.qr_code_url && (
                            <div className="px-2 py-1 rounded-full text-xs font-semibold bg-green-500 flex items-center gap-1">
                              <QrCode className="h-3 w-3" />
                            </div>
                          )}
                          {expiryInfo?.urgent && (
                            <div className={`px-2 py-1 rounded-full text-xs font-semibold bg-black/60 ${expiryInfo.color} flex items-center gap-1`}>
                              <Clock className="h-3 w-3" />
                              {expiryInfo.text}
                            </div>
                          )}
                          {property.images && property.images.length > 0 && (
                            <div className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-500 flex items-center gap-1">
                              <Camera className="h-3 w-3" />
                              {property.images.length}
                            </div>
                          )}
                        </div>

                        <div className="absolute bottom-3 right-3 flex gap-2">
                          <ShareButton
                            propertyId={property.id}
                            propertyUrl={getPropertyUrl(property)}
                            propertyTitle={property.title}
                            propertyPrice={formatPrice(property.price, property.currency)}
                            agentName={agent?.name}
                            size="md"
                          />
                          <button
                            onClick={() => handleQRClick(property)}
                            disabled={generatingQR === property.id}
                            className="p-2 rounded-lg bg-purple-500/80 hover:bg-purple-500 transition-colors disabled:opacity-50"
                            title={t('viewQR')}
                          >
                            {generatingQR === property.id ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <QrCode className="h-4 w-4 text-white" />
                            )}
                          </button>
                          <button
                            onClick={() => openImageUpload(property)}
                            className="p-2 rounded-lg bg-blue-500/80 hover:bg-blue-500 transition-colors"
                            title={t('manageImages')}
                          >
                            <Camera className="h-4 w-4 text-white" />
                          </button>
                        </div>
                      </div>

                      <div className="p-4">
                        <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{property.title}</h3>
                        <p className="text-2xl font-bold text-blue-400 mb-3">{formatPrice(property.price, property.currency)}</p>

                        <div className="flex items-center gap-3 text-white/70 text-sm mb-4">
                          {property.bedrooms && (
                            <div className="flex items-center gap-1">
                              <Bed className="h-4 w-4" />
                              {property.bedrooms}
                            </div>
                          )}
                          {property.bathrooms && (
                            <div className="flex items-center gap-1">
                              <Bath className="h-4 w-4" />
                              {property.bathrooms}
                            </div>
                          )}
                          {property.half_bathrooms > 0 && (
                            <div className="flex items-center gap-1">
                              <Toilet className="h-4 w-4" />
                              {property.half_bathrooms}½
                            </div>
                          )}
                          {property.area_m2 && (
                            <div className="flex items-center gap-1">
                              <Square className="h-4 w-4" />
                              {property.area_m2}m²
                            </div>
                          )}
                          {property.parking_spots && (
                            <div className="flex items-center gap-1">
                              <Car className="h-4 w-4" />
                              {property.parking_spots}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
                          <MapPin className="h-4 w-4" />
                          <span className="line-clamp-1">
                            {property.city && property.state
                              ? `${property.city}, ${property.state}`
                              : property.zone}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
                          <Eye className="h-4 w-4" />
                          <span>{t('views', { count: property.views_count })}</span>
                        </div>

                        {!isSeller && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleOpenForm(property)}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                            >
                              <Edit3 className="h-4 w-4" />
                              {t('edit')}
                            </button>
                            <button
                              onClick={() => deleteProperty(property.id)}
                              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        {isSeller && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleOpenForm(property, true)}                              
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                            >
                              <Eye className="h-4 w-4" />
                              {t('viewDetails')}
                            </button>
                          </div>
                        )}
                      </div>
                    </GlassCard>
                  </motion.div>
                )
              })}
            </div>

            {/* Sección Vencidas */}
            {showExpired && expiredProperties.length > 0 && (
              <>
                <div className="flex items-center gap-4 my-8">
                  <div className="flex-1 h-px bg-red-500/40"></div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 rounded-full border border-red-500/30">
                    <Clock className="h-4 w-4 text-red-400" />
                    <span className="text-red-400 font-semibold text-sm">
                      {t('expiredProperties', { count: expiredProperties.length })}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-red-500/40"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-75">
                  {expiredProperties.map((property) => {
                    const expiryInfo = property.expires_at ? getTimeUntilExpiry(property.expires_at) : null
                    return (
                      <motion.div
                        key={property.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="group"
                      >
                        <GlassCard className="overflow-hidden hover:shadow-2xl transition-shadow border border-red-500/30">
                          <div className="relative h-48 bg-gradient-to-br from-gray-700 to-gray-900 overflow-hidden">
                            {property.featured_image || property.images?.[0] ? (
                              <img
                                src={property.featured_image || property.images[0]}
                                alt={property.title}
                                className="w-full h-full object-cover grayscale-[50%]"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <MapPin className="h-16 w-16 text-white/30" />
                              </div>
                            )}
                            <div className="absolute top-0 right-0 z-10">
                              <div className="bg-red-600 text-white text-xs font-bold shadow-lg"
                                   style={{ position: 'absolute', top: '12px', right: '-20px', transform: 'rotate(45deg)', width: '120px', textAlign: 'center' }}>
                                {t('badgeExpired')}
                              </div>
                            </div>
                            <div className="absolute top-3 left-3 flex flex-col gap-2">
                              <div className="px-2 py-1 rounded-full text-xs font-semibold bg-red-600/80 text-white flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {expiryInfo?.text}
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{property.title}</h3>
                            <p className="text-2xl font-bold text-red-400/70 mb-3">{formatPrice(property.price, property.currency)}</p>
                            <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
                              <MapPin className="h-4 w-4" />
                              <span className="line-clamp-1">
                                {property.city && property.state
                                  ? `${property.city}, ${property.state}`
                                  : property.zone}
                              </span>
                            </div>
                            {!isSeller && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleReactivate(property)}
                                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 rounded-lg text-amber-400 transition-colors"
                                >
                                  <Calendar className="h-4 w-4" />
                                  {t('reactivate')}
                                </button>
                                <button
                                  onClick={() => deleteProperty(property.id)}
                                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </GlassCard>
                      </motion.div>
                    )
                  })}
                </div>
              </>
            )}

            {/* Sección No Disponibles (Vendidas/Rentadas) */}
            {unavailableProperties.length > 0 && (
              <>
                <div className="flex items-center gap-4 my-8">
                  <div className="flex-1 h-px bg-amber-500/40"></div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full border border-amber-500/30">
                    <Eye className="h-4 w-4 text-amber-400" />
                    <span className="text-amber-400 font-semibold text-sm">
                      {t('unavailable', { count: unavailableProperties.length })} 
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-amber-500/40"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-75">
                  {unavailableProperties.map((property) => (
                    <motion.div
                      key={property.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="group"
                    >
                      <GlassCard className="overflow-hidden hover:shadow-2xl transition-shadow border border-amber-500/30">
                        <div className="relative h-48 bg-gradient-to-br from-gray-700 to-gray-900 overflow-hidden">
                          {property.featured_image || property.images?.[0] ? (
                            <img
                              src={property.featured_image || property.images[0]}
                              alt={property.title}
                              className="w-full h-full object-cover grayscale-[50%]"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <MapPin className="h-16 w-16 text-white/30" />
                            </div>
                          )}
                          <div className="absolute top-0 right-0 z-10">
                            <div className={`text-white text-xs font-bold shadow-lg ${property.status === 'sold' ? 'bg-amber-600' : 'bg-blue-600'}`}
                                 style={{ position: 'absolute', top: '12px', right: '-20px', transform: 'rotate(45deg)', width: '120px', textAlign: 'center', padding: '2px 0' }}>
                              {property.status === 'sold' ? t('badgeSold') : t('badgeRented')}
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{property.title}</h3>
                          <p className="text-2xl font-bold text-amber-400/70 mb-3">{formatPrice(property.price, property.currency)}</p>
                          <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
                            <MapPin className="h-4 w-4" />
                            <span className="line-clamp-1">
                              {property.city && property.state ? `${property.city}, ${property.state}` : property.zone}
                            </span>
                          </div>
                          {!isSeller && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleOpenForm(property)}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 rounded-lg text-amber-400 transition-colors"
                              >
                                <Edit3 className="h-4 w-4" />
                                {t('edit')}
                              </button>
                              <button
                                onClick={() => deleteProperty(property.id)}
                                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </GlassCard>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Modal Reactivar — solo fecha */}
      <AnimatePresence>
        {showReactivateModal && reactivatingProperty && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md"
            >
              <GlassCard className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-white">{t('reactivateProperty')}</h2>
                    <p className="text-white/60 text-sm mt-1 line-clamp-1">{reactivatingProperty.title}</p>
                  </div>
                  <button
                    onClick={() => setShowReactivateModal(false)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-white font-semibold mb-1">{t('newPublishDate')}</p>
                      <p className="text-white/60 text-sm">{t('newPublishDateHint')}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-white mb-2">{t('expiryDateLabel')}</label>
                  <input
                    type="date"
                    value={reactivateDate}
                    onChange={(e) => setReactivateDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-lg"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowReactivateModal(false)}
                    className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-lg text-white font-semibold transition-colors"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={submitReactivate}
                    disabled={!reactivateDate}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 rounded-lg text-white font-semibold transition-all"
                  >
                    {t('reactivate')}
                  </button>
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form Modal — Nueva/Editar Propiedad */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl my-8"
            >
              <GlassCard className="p-6">
                {/* Header sticky con backdrop sólido para evitar traslape */}
                <div className="flex justify-between items-center mb-6 sticky top-0 z-10 bg-slate-900/95 backdrop-blur-md -mx-6 px-6 py-4 border-b border-white/10">
                  <h2 className="text-2xl font-bold text-white">
                    {viewOnly ? t('modalViewProperty') : (editingProperty ? t('modalEditProperty') : t('newProperty'))}
                  </h2>
                  <button
                    onClick={() => { setShowForm(false); setViewOnly(false) }}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="h-6 w-6 text-white" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className={`space-y-6 ${viewOnly ? 'pointer-events-none opacity-70' : ''}`}>

                  {/* ── BLOQUE 1: Propósito, Estado, Prioridad (primero y visible) ── */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Star className="h-4 w-4 text-blue-400" />
                      {t('formClassification')}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-white mb-2">{t('formPurpose')}</label>
                        <select
                          required
                          value={formData.purpose}
                          onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                          className={`w-full px-4 py-2 bg-white/10 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            !formData.purpose ? 'border-yellow-500/60' : 'border-white/20'
                          }`}
                        >
                          <option value="" className="bg-slate-800 text-white/50">{t('selectPurpose')}</option>
                          <option value="venta" className="bg-slate-800 text-white">{t('sale')}</option>
                          <option value="renta" className="bg-slate-800 text-white">{t('rent')}</option>
                          <option value="inversion" className="bg-slate-800 text-white">{t('investment')}</option>
                          <option value="leasing" className="bg-slate-800 text-white">{t('leasing')}</option>
                        </select>
                        {!formData.purpose && (
                          <p className="text-yellow-400 text-xs mt-1">{t('requiredField')}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formConstructionStatus')}</label>
                        <select
                          value={formData.construction_status}
                          onChange={(e) => setFormData({ ...formData, construction_status: e.target.value })}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="" className="bg-slate-800 text-white">{t('csUnspecified')}</option>
                          <option value="preventa" className="bg-slate-800 text-white">{t('csPresale')}</option>
                          <option value="en_construccion" className="bg-slate-800 text-white">{t('csUnderConstruction')}</option>
                          <option value="terminado" className="bg-slate-800 text-white">{t('csFinished')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formPriority')}</label>
                        <select
                          value={formData.priority_level}
                          onChange={(e) => setFormData({ ...formData, priority_level: e.target.value as any })}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="standard" className="bg-slate-800 text-white">{t('prioStandard')}</option>
                          <option value="premium" className="bg-slate-800 text-white">{t('prioPremium')}</option>
                          <option value="featured" className="bg-slate-800 text-white">{t('prioFeatured')}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* ── BLOQUE 2: Título y Tipo ── */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white mb-2">{t('formTitle')}</label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2">{t('formType')}</label>
                      <select
                        required
                        value={formData.property_type}
                        onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Casa" className="bg-slate-800 text-white">{t('typeHouse')}</option>
                        <option value="Departamento" className="bg-slate-800 text-white">{t('typeApartment')}</option>
                        <option value="Terreno" className="bg-slate-800 text-white">{t('typeLand')}</option>
                        <option value="Comercial" className="bg-slate-800 text-white">{t('typeCommercial')}</option>
                        <option value="Oficina" className="bg-slate-800 text-white">{t('typeOffice')}</option>
                        <option value="Local" className="bg-slate-800 text-white">{t('typeStore')}</option>
                        <option value="Bodega" className="bg-slate-800 text-white">{t('typeWarehouse')}</option>
                      </select>
                    </div>
                  </div>

                  {/* ── BLOQUE 3: Descripción ── */}
                  <div>
                    <label className="block text-white mb-2">{t('formDescription')}</label>
                    <textarea
                      required
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* ── BLOQUE 4: Precio ── */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-white mb-2">{t('formPrice')}</label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2">{t('formCurrency')}</label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="MXN" className="bg-slate-800 text-white">MXN</option>
                        <option value="USD" className="bg-slate-800 text-white">USD</option>
                      </select>
                    </div>
                  </div>

                  {/* ── BLOQUE 5: Características ── */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-white mb-2">{t('formBedrooms')}</label>
                      <input
                        type="number"
                        value={formData.bedrooms}
                        onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2">{t('formBathrooms')}</label>
                      <input
                        type="number"
                        value={formData.bathrooms}
                        onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2">{t('formHalfBaths')}</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.half_bathrooms}
                        onChange={(e) => setFormData({ ...formData, half_bathrooms: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2">{t('formBuiltArea')}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.area_m2}
                        onChange={(e) => setFormData({ ...formData, area_m2: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-white mb-2">{t('formLandArea')}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.area_total}
                        onChange={(e) => setFormData({ ...formData, area_total: e.target.value })}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* ── BLOQUE 6: Características para Portales ── */}
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <Building className="h-5 w-5 text-purple-400 mt-0.5" />
                      <div>
                        <h4 className="text-white font-semibold mb-1">{t('formPortalTitle')}</h4>
                        <p className="text-white/70 text-sm">{t('formPortalDesc')}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className="block text-white mb-2"><Car className="h-4 w-4 inline mr-1" />{t('formParking')}</label>
                        <input type="number" min="0" value={formData.parking_spots}
                          onChange={(e) => setFormData({ ...formData, parking_spots: e.target.value })}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formYearBuilt')}</label>
                        <input type="number" min="1900" max={new Date().getFullYear()} value={formData.year_built}
                          onChange={(e) => setFormData({ ...formData, year_built: e.target.value })}
                          placeholder={t('yearBuiltPlaceholder')}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formFloor')}</label>
                        <input type="number" min="0" value={formData.floor_number}
                          onChange={(e) => setFormData({ ...formData, floor_number: e.target.value })}
                          placeholder={t('floorPlaceholder')}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formTotalFloors')}</label>
                        <input type="number" min="1" value={formData.total_floors}
                          onChange={(e) => setFormData({ ...formData, total_floors: e.target.value })}
                          placeholder={t('totalFloorsPlaceholder')}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-white mb-2"><DollarSign className="h-4 w-4 inline mr-1" />{t('formMaintenance')}</label>
                        <input type="number" step="0.01" min="0" value={formData.maintenance_fee}
                          onChange={(e) => setFormData({ ...formData, maintenance_fee: e.target.value })}
                          placeholder={t('maintenancePlaceholder')}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      </div>
                      <div>
                        <label className="block text-white mb-2"><Sofa className="h-4 w-4 inline mr-1" />{t('formFurnished')}</label>
                        <select value={formData.furnished}
                          onChange={(e) => setFormData({ ...formData, furnished: e.target.value })}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
                          <option value="" className="bg-slate-800 text-white">{t('furnishedNo')}</option>
                          <option value="amueblado" className="bg-slate-800 text-white">{t('furnishedYes')}</option>
                          <option value="semi_amueblado" className="bg-slate-800 text-white">{t('furnishedSemi')}</option>
                          <option value="sin_amueblar" className="bg-slate-800 text-white">{t('furnishedNone')}</option>
                        </select>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-3 text-white cursor-pointer p-2 bg-white/5 rounded-lg w-full justify-center">
                          <input type="checkbox" checked={formData.pets_allowed}
                            onChange={(e) => setFormData({ ...formData, pets_allowed: e.target.checked })}
                            className="w-5 h-5 rounded" />
                          <PawPrint className="h-4 w-4" />
                          {t('formPetsAllowed')}
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-white mb-2">{t('formAmenities')}</label>
                      <input type="text" value={formData.amenities}
                        onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                        placeholder={t('amenitiesPlaceholder')}
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500" />
                      <p className="text-white/50 text-xs mt-1">{t('amenitiesHint')}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {AMENITY_SUGGESTIONS.slice(0, 8).map(amenity => (
                          <button key={amenity} type="button"
                            onClick={() => {
                              const current = formData.amenities ? formData.amenities.split(',').map(a => a.trim()) : []
                              if (!current.includes(amenity)) {
                                setFormData({ ...formData, amenities: current.length > 0 ? `${formData.amenities}, ${amenity}` : amenity })
                              }
                            }}
                            className="px-2 py-1 text-xs bg-purple-500/20 hover:bg-purple-500/40 text-purple-300 rounded transition-colors">
                            + {amenity}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── BLOQUE 7: Ubicación ── */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <MapPin className="h-5 w-5 text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="text-white font-semibold mb-1">{t('formLocationTitle')}</h4>
                        <p className="text-white/70 text-sm">
                          {t('formLocationDesc')}
                          <br />
                          <span className="text-blue-400">{t('formLocationTip')}</span>
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-white mb-2">{t('formLatitude')}</label>
                        <input type="text" inputMode="decimal" required placeholder={t('latPlaceholder')}
                          value={formData.latitude}
                          onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formLongitude')}</label>
                        <input type="text" inputMode="tel" required placeholder={t('lngPlaceholder')}
                          value={formData.longitude}
                          onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>

                      <div className="flex items-end">
                        <button type="button" onClick={fetchLocationFromCoords}
                          disabled={loadingGeo || !formData.latitude || !formData.longitude}
                          className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 disabled:cursor-not-allowed rounded-lg text-white font-semibold transition-colors flex items-center justify-center gap-2">
                          {loadingGeo ? <><Loader2 className="h-4 w-4 animate-spin" />{t('gettingLocation')}</> : <><MapPin className="h-4 w-4" />{t('getLocation')}</>}
                        </button>
                      </div>
                    </div>

                    {geoError && (
                      <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
                        <AlertCircle className="h-4 w-4" />{geoError}
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-white mb-2">{t('formCity')}</label>
                        <input type="text" readOnly value={formData.city}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white/70 cursor-not-allowed" placeholder={t('autocompletes')} />
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formState')}</label>
                        <input type="text" readOnly value={formData.state}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white/70 cursor-not-allowed" placeholder={t('autocompletes')} />
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formCountry')}</label>
                        <input type="text" readOnly value={formData.country}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white/70 cursor-not-allowed" placeholder={t('autocompletes')} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white mb-2">{t('formZone')}</label>
                        <input type="text" required value={formData.zone}
                          onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                          placeholder={t('zonePlaceholder')}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div>
                        <label className="block text-white mb-2">{t('formAddress')}</label>
                        <input type="text" value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          placeholder={t('addressPlaceholderForm')}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  </div>

                  {/* ── BLOQUE 8: Control de Publicación ── */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                      <div>
                        <h4 className="text-white font-semibold mb-1">{t('formPublishTitle')}</h4>
                        <p className="text-white/70 text-sm">
                          {t('formPublishDesc', { days: EXPIRY_CONFIG.MAX_DAYS })}
                          {!editingProperty && t('formPublishAutoDate')}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white mb-2">{t('formExpiryDate')}</label>
                        <input type="date" value={formData.expires_at}
                          onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-3 text-white cursor-pointer p-3 bg-white/5 rounded-lg w-full">
                          <input type="checkbox" id="public_listing" checked={formData.public_listing}
                            onChange={(e) => setFormData({ ...formData, public_listing: e.target.checked })}
                            className="w-5 h-5 rounded" />
                          {t('publishOnSite')}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* ── Botones ── */}
                  <div className="flex gap-4 pt-2">
                    <button type="button"
                      onClick={() => { setShowForm(false); setViewOnly(false) }}
                      className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white font-semibold transition-colors">
                      {viewOnly ? t('close') : t('cancel')}
                    </button>
                    {!viewOnly && (
                      <button type="submit"
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-xl text-white font-semibold transition-all">
                        {editingProperty ? t('saveChanges') : t('createProperty')}
                      </button>
                    )}
                  </div>
                </form>
              </GlassCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* QR Modal */}
      {selectedPropertyForQR && qrBase64Data && (
        <QRModal
          isOpen={showQRModal}
          onClose={() => { setShowQRModal(false); setSelectedPropertyForQR(null); setQrBase64Data(null) }}
          qrCodeUrl={qrBase64Data.base64}
          propertyTitle={selectedPropertyForQR.title}
          propertyUrl={qrBase64Data.url}
        />
      )}

      {/* Image Upload Modal */}
      {selectedPropertyForImages && (
        <ImageUploadModal
          isOpen={showImageModal}
          onClose={() => { setShowImageModal(false); setSelectedPropertyForImages(null) }}
          propertyId={selectedPropertyForImages.id}
          propertyTitle={selectedPropertyForImages.title}
          existingImages={selectedPropertyForImages.images || []}
          featuredImage={selectedPropertyForImages.featured_image || ''}
          onUploadComplete={fetchProperties}
          readOnly={isSeller}
        />
      )}
    </DashboardLayout>
  )
}

export default PropertiesPage
